import {Injectable} from '@angular/core';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4plugins_wordCloud from '@amcharts/amcharts4/plugins/wordCloud';
import * as _ from 'lodash';
import {XYChart} from '@amcharts/amcharts4/charts';

import * as am4maps from '@amcharts/amcharts4/maps';
import am4geodata_worldLow from '@amcharts/amcharts4-geodata/worldLow';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { cursorTo } from 'readline';

am4core.options.minPolylineStep = 8;
am4core.addLicense('CH239567974');
am4core.useTheme(am4themes_animated);

// Disabled to improve performance, will re-enable at a later time
// am4core.useTheme(am4themes_animated);

// Enable sequential loading of charts
am4core.options.queue = true;
// Disabling animations for performance hit
am4core.unuseAllThemes();



@Injectable({
  providedIn: 'root'
})
export class DrawChartService {

  constructor() {
  }

  buildDateAxis(chart: am4charts.XYChart): am4charts.DateAxis {
    const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
    dateAxis.renderer.grid.template.location = 0;
    dateAxis.renderer.minGridDistance = 60;
    dateAxis.groupData = true;
    dateAxis.groupCount = 100;
    dateAxis.renderer.minLabelPosition = 0.05;
    dateAxis.renderer.maxLabelPosition = 0.95;
    return dateAxis;
  }

  buildBullets(series: am4charts.Series): am4core.Circle {
    const bullet = series.bullets.push(new am4core.Circle());
    bullet.fill = new am4core.InterfaceColorSet().getFor('background');
    bullet.fillOpacity = 1;
    bullet.strokeWidth = 2;
    bullet.radius = 4;
    return bullet;
  }

  private createBarSeries(chart, field: string, name: string, xValueField: string, hiddenInLegend: boolean): void {
    const series = chart.series.push(new am4charts.ColumnSeries());
    series.dataFields.valueY = field;
    series.dataFields.dateX = xValueField;

    series.name = name;
    series.tooltipText = `{name}: [bold]{valueY}[/]\n${xValueField}: [bold]{dateX}`;

    series.strokeWidth = 2;
    if (hiddenInLegend) {
      series.hiddenInLegend = false;
    }
    series.tooltip.pointerOrientation = 'vertical';
    series.width = 4;
    series.groupFields.valueY = 'max';
    series.stacked = true;
    series.dataItems.template.locations.categoryX = 0.5;

    // Set the cursor
    chart.cursor = new am4charts.XYCursor();
    return series;
  }

  drawMultiBarChart(res, chart, widget, chartId?: string) {
    const valueXAxis = widget.params.athenaStatement?.standard[0]?.select_statement[0]?.alias ?? [];

    if (!chartId) {
      chartId = widget.widget_id;
    }

    if (!chart) {
      chart = am4core.create(chartId, am4charts.XYChart);
      chart.height = am4core.percent(70);

      // Add a legend
      // chart.legend = new am4charts.Legend();
      // chart.legend.position = 'absolute';
      // chart.legend.x = 0;
      // chart.legend.y = 0;
      // chart.legend.maxHeight = 25;
      // chart.legend.scrollable = true;
      // Build date axis
      const dateAxis = this.buildDateAxis(chart);
      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.min = 0;

      // set the scrollbar
      chart.scrollbarX = new am4core.Scrollbar();
      // Add a legend
      chart.legend = new am4charts.Legend();
    }

    const snapTo = chart?.cursor?.snapToSeries || [];
    if (res.length > 0) {
      const row = res[0];
      Object.keys(row).forEach((key: string) => {
        // If there's already a series for this key, skip it
        if (chart.series.values.find(item => item.dataFields.valueY === key)) {
          return;
        }
        if (key !== valueXAxis) {
          snapTo.push(this.createBarSeries(chart, key, key, valueXAxis, true));
        }
      });
    }

    // chart.cursor.snapToSeries = snapTo;
    chart.data = res = _.sortBy(res, [(o) => o[`${valueXAxis}`]]);

    return chart;
  }

  private createLineSeries(chart, field: string, name: string, xValueField: string, hiddenInLegend: boolean): am4charts.LineSeries {
    const series = chart.series.push(new am4charts.LineSeries());
    series.dataFields.valueY = field;
    series.dataFields.dateX = xValueField;
    series.name = name;
    series.tooltipText = `{name}: [bold]{valueY}[/]\n${xValueField}: [bold]{dateX}`;
    series.strokeWidth = 2;
    series.minBulletDistance = 5;
    if (hiddenInLegend) {
      series.hiddenInLegend = false;
    }

    // Create Bullet for pinpointing data in line chart
    this.buildBullets(series);

    // Set the cursor
    chart.cursor = new am4charts.XYCursor();

    return series;
  }

  drawMinimalMultiLineChart(res, chartId): XYChart {
    console.log('drowing here')
    am4core.useTheme(this.lumenColorsTheme);
    // set up value axis
    const valueXAxis = res.axes.xAxisProperty;
    // set up date axis
    const data = res.data;
    // format objects with date objects
    const chart = am4core.create(chartId, am4charts.XYChart);
    // set up line series
    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());

    // const dateAxis = this.buildDateAxis(chart);
    const dateAxis = chart.xAxes.push(new am4charts.DateAxis());

    const snapTo = [];
    if (data.length > 0) {
      const row = data[0];
      Object.keys(row).forEach((key: string) => {
        if (key !== valueXAxis) {
          snapTo.push(this.createLineSeries(chart, key, key, valueXAxis, false));
        }
      });
    }
    chart.cursor.snapToSeries = snapTo;
    // Add a legend
    chart.legend = new am4charts.Legend();
    chart.legend.position = 'bottom';
    chart.legend.maxHeight = 25;
    chart.legend.scrollable = true;
    // set data
    chart.data = data;
    return chart;
  }

  drawMultiLineChart(res, chart, widget, chartId?: string) {
    const valueXAxis = widget.params.athenaStatement.standard[0].select_statement[0].alias;
    if (!chartId) {
      chartId = widget.widget_id;
    }
    if (!chart) {
      chart = am4core.create(chartId, am4charts.XYChart);

      // build the legend
      // chart.legend = new am4charts.Legend();
      var legendContainer = am4core.create(`${chartId}-legend`, am4core.Container);
      legendContainer.width = am4core.percent(100);
      legendContainer.height = am4core.percent(100);
      chart.legend.parent = legendContainer;

      // Add a legend
      // chart.legend = new am4charts.Legend();
      // chart.legend.position = 'bottom';
      // chart.legend.maxHeight = 25;
      // chart.legend.scrollable = true;
      // Build date axis
      const dateAxis = this.buildDateAxis(chart);

      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());

      // set the scrollbar
      chart.scrollbarX = new am4core.Scrollbar();
    }

    const snapTo = chart?.cursor?.snapToSeries || [];
    if (res.length > 0) {
      const row = res[0];
      Object.keys(row).forEach((key: string) => {
        // If there's already a series for it, skip
        if (chart.series.values.find(item => item.dataFields.valueY === key)) {
          return;
        }
        if (key !== valueXAxis) {
          snapTo.push(this.createLineSeries(chart, key, key, valueXAxis, true));
        }
      });
    }
    chart.cursor.snapToSeries = snapTo;

    chart.data = res = _.sortBy(res, [(o) => o[`${valueXAxis}`]]);

    return chart;
  }

  drawMapWidget(res, chart, widget, chartId?: string) {
    // Define marker path
    const targetSVG = 'M9,0C4.029,0,0,4.029,0,9s4.029,9,9,9s9-4.029,9-9S13.971,0,9,0z M9,15.93 c-3.83,0-6.93-3.1-6.93-6.93S5.17,2.07,9,2.07s6.93,3.1,6.93,6.93S12.83,15.93,9,15.93 M12.5,9c0,1.933-1.567,3.5-3.5,3.5S5.5,10.933,5.5,9S7.067,5.5,9,5.5 S12.5,7.067,12.5,9z';
    const interfaceColors = new am4core.InterfaceColorSet();

    if (!chartId) {
      chartId = widget.widget_id;
    }
    if (!chart) {
      // Create map instance
      chart = am4core.create(chartId, am4maps.MapChart);
      // const interfaceColors = new am4core.InterfaceColorSet();

      // Set map definition
      chart.geodata = am4geodata_worldLow;

      // Set projection
      chart.projection = new am4maps.projections.Mercator();

    // Add zoom control
      chart.zoomControl = new am4maps.ZoomControl();

    // Set initial zoom
      chart.homeZoomLevel = 1;
      chart.homeGeoPoint = {
        latitude: 35,
        longitude: 0
      };
    // Add lines
      const lineSeries = chart.series.push(new am4maps.MapLineSeries());
      lineSeries.dataFields.multiGeoLine = 'multiGeoLine';

      const lineTemplate = lineSeries.mapLines.template;
      lineTemplate.nonScalingStroke = true;
      lineTemplate.arrow.nonScaling = true;
      lineTemplate.arrow.width = 4;
      lineTemplate.arrow.height = 6;
      lineTemplate.stroke = interfaceColors.getFor('alternativeBackground');
      lineTemplate.fill = interfaceColors.getFor('alternativeBackground');
      lineTemplate.line.strokeOpacity = 0.4;
    }
    this.mapImageSeries(targetSVG, interfaceColors, chart, res);
    this.mapLineSeries(chart, res);

    return chart;
  }

  private mapImageSeries(targetSVG, interfaceColors, chart, res) {
    // Create map polygon series
    const polygonSeries = chart.series.push(new am4maps.MapPolygonSeries());
    polygonSeries.exclude = ['AQ'];
    polygonSeries.useGeodata = true;
    polygonSeries.mapPolygons.template.nonScalingStroke = true;

    // Add images
    const imageSeries = chart.series.push(new am4maps.MapImageSeries());
    const imageTemplate = imageSeries.mapImages.template;
    imageTemplate.tooltipText = '{title}\nLatitude: {latitude}\nLongitude: {longitude}';
    imageTemplate.nonScaling = true;

    const marker = imageTemplate.createChild(am4core.Sprite);
    marker.path = targetSVG;
    marker.horizontalCenter = 'middle';
    marker.verticalCenter = 'middle';
    marker.scale = 0.7;
    marker.fill = interfaceColors.getFor('alternativeBackground');

    imageTemplate.propertyFields.latitude = 'latitude';
    imageTemplate.propertyFields.longitude = 'longitude';

    let tempImageSeries =  _.flatten(_.map(res, 'imageSeriesData'));
    tempImageSeries = tempImageSeries.map((value) => ({
      ...value,
      svgPath: targetSVG,
      scale: 0.5
    }));

    imageSeries.data = tempImageSeries;
    return imageSeries;
  }

  private mapLineSeries(chart, res) {
    const lineSeries = chart.series.push(new am4maps.MapLineSeries());

    const tempLineSeries = _.map(res, 'lineSeriesData');
    lineSeries.data = tempLineSeries.map((value) => ({multiGeoLine: [value] }));
    return lineSeries;
  }

  drawLineChart(res, chart, widget, chartId?: string) {
    const valueXAxis = widget.params.athenaStatement.standard[0].select_statement[0].alias;
    const valueYAxis = widget.params.athenaStatement.standard[0].select_statement[1].alias;

    if (!chartId) {
      chartId = widget.widget_id;
    }
    // create the chart
    if (!chart) {
      // Initialize the chart
      chart = am4core.create(chartId, am4charts.XYChart);

      // build the legend
      chart.legend = new am4charts.Legend();
      var legendContainer = am4core.create(`${chartId}-legend`, am4core.Container);
      legendContainer.width = am4core.percent(100);
      legendContainer.height = am4core.percent(100);
      chart.legend.parent = legendContainer;
      
      // Add a legend
      // chart.legend = new am4charts.Legend();
      // chart.legend.position = 'bottom';
      // chart.legend.maxHeight = 25;
      // chart.legend.scrollable = true;
      // Build date axis
      const dateAxis = this.buildDateAxis(chart);

      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.title.text = widget.title;

      /// Create the series
      const series = chart.series.push(new am4charts.LineSeries());
      series.dataFields.dateX = valueXAxis;
      series.dataFields.valueY = valueYAxis;

      series.tooltipText = '{value}';
      series.tooltip.pointerOrientation = 'vertical';
      series.width = 4;
      series.minBulletDistance = 5;
      series.groupFields.valueY = 'max';

      // Create Bullet for pinpointing data in line chart
      this.buildBullets(series);

      // Set the cursor
      chart.cursor = new am4charts.XYCursor();
      chart.cursor.snapToSeries = series;
      chart.cursor.xAxis = dateAxis;

      // set the scrollbar
      chart.scrollbarX = new am4core.Scrollbar();
    }

    chart.data = res;
    return chart;

  }

  drawBarChart(res, chart, widget, chartId?: string) {
    const catX = widget.params.athenaStatement.standard[0].select_statement[0].alias;
    const valY = widget.params.athenaStatement.standard[0].select_statement[1].alias;

    if (!chartId) {
      chartId = widget.widget_id;
    }
    // If a bar chart has been converted to a break axis chart, the break axis will persist, until we clear it
    if (chart && chart.yAxes && chart.yAxes.values[0] && chart.yAxes.values[0].axisBreaks.length > 0) {
      chart.yAxes.values[0].axisBreaks.clear();
    }
    // create the chart
    if (!chart) {
      chart = am4core.create(chartId, am4charts.XYChart);

      // build the legend
      chart.legend = new am4charts.Legend();
      var legendContainer = am4core.create(`${chartId}-legend`, am4core.Container);
      legendContainer.width = am4core.percent(100);
      legendContainer.height = am4core.percent(100);
      chart.legend.parent = legendContainer;

      // Add a legend
      // chart.legend = new am4charts.Legend();
      // chart.legend.position = 'bottom';
      // chart.legend.maxHeight = 25;
      // chart.legend.scrollable = true;
      // Build date axis
      const dateAxis = this.buildDateAxis(chart);

      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      const title = chart.titles.create();
      title.text = widget.title;

      // Create the series
      const series = chart.series.push(new am4charts.ColumnSeries());
      series.dataFields.valueY = valY;
      series.dataFields.dateX = catX;

      series.tooltipText = '{value}';
      series.tooltip.pointerOrientation = 'vertical';
      series.width = 4;
      series.groupFields.valueY = 'max';

      // Set the cursor
      chart.cursor = new am4charts.XYCursor();
      chart.cursor.snapToSeries = series;
      chart.cursor.xAxis = dateAxis;

      // set the scrollbar
      chart.scrollbarX = new am4core.Scrollbar();
    }

    chart.data = res;
    return chart;

  }

  drawAxisBreakChart(res, chart, widget, mapped, lowerStepIndex, higherStepIndex) {
    const yAxisProp = widget.params.yAxisAction;
    // create the chart
    if (chart) {
      chart = am4core.create(widget.widget_id, am4charts.XYChart);
      chart.hiddenState.properties.opacity = 0;
      // create axes
      const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
      dateAxis.renderer.minGridDistance = 60;
      dateAxis.groupData = true;
      dateAxis.groupCount = 300;

      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.title.text = `${widget.params.field.friendly.toUpperCase()} - ${yAxisProp.toUpperCase()}`;

      // axis break
      const axisBreak = valueAxis.axisBreaks.create();
      const lowerStep = parseInt(res[lowerStepIndex][yAxisProp], 10);
      const higherStep = parseInt(res[higherStepIndex][yAxisProp], 10);

      valueAxis.min = 0;
      valueAxis.max = Math.ceil(parseInt(mapped[mapped.length - 1].value, 10) + 1);

      axisBreak.startValue = Math.ceil(lowerStep * 1.1);
      axisBreak.endValue = Math.ceil(higherStep * 0.9);

      // fixed axis break
      const d = (axisBreak.endValue - axisBreak.startValue) / (valueAxis.max - valueAxis.min);
      axisBreak.breakSize = 0.05 * (1 - d) / d; // 0.05 means that the break will take 5% of the total value axis height

      // make break expand on hover
      const hoverState = axisBreak.states.create('hover');
      hoverState.properties.breakSize = 1;
      hoverState.properties.opacity = 0.1;
      hoverState.transitionDuration = 1500;

      axisBreak.defaultState.transitionDuration = 1000;

      // Create the series
      const series = chart.series.push(new am4charts.ColumnSeries());
      series.dataFields.dateX = 'dateObject';
      series.dataFields.valueY = 'count';
      series.tooltipText = '{value}';
      series.tooltip.pointerOrientation = 'vertical';
      series.width = 4;

      // Set the cursor
      chart.cursor = new am4charts.XYCursor();
      chart.cursor.snapToSeries = series;
      chart.cursor.xAxis = dateAxis;

      // set the scrollbar
      chart.scrollbarX = new am4core.Scrollbar();
    }
    chart.data = res;
    return chart;
  }

  findBiggestDiff(arr: any[]): any {
    if (!arr.length) {
      return false;
    }
    let biggestStep = 0;
    let lowerStep = 0;
    for (let i = 0; i < arr.length - 1; i++) {
      if (Math.abs(arr[i].value - arr[i + 1].value) > biggestStep) {
        biggestStep = Math.abs(arr[i].value - arr[i + 1].value);
        lowerStep = i;
      }
    }
    return {lowerStep: arr[lowerStep].index, higherStep: arr[lowerStep + 1].index};
  }

  drawPieChart(res, chart, widget, chartId?: string) {
    if (!chartId) {
      chartId = widget.widget_id;
    }
    if (!chart) {
      chart = am4core.create(chartId, am4charts.PieChart);

      // generating the pie series
      const pieSeries = chart.series.push(new am4charts.PieSeries());
      pieSeries.radius = am4core.percent(100);
      pieSeries.dataFields.value = 'count';
      pieSeries.dataFields.category = 'name';

      // Set the label
      pieSeries.alignLabels = true;
      const title = chart.titles.create();
      title.text = widget.title;
      title.fontSize = 16;
      title.fontWeight = 'bold';

      // Add a legend
      chart.legend = new am4charts.Legend();
      chart.legend.scrollable = true;
      chart.legend.position = 'right';
    }
    chart.data = res;

    return chart;
  }

  drawSingleMetric(res, chart, widget, chartId?: string): am4plugins_wordCloud.WordCloud {
    for (const key in res[0]) {
      if (res[0].hasOwnProperty(key)) {
        res[0][key] = parseInt(Object.values(res)[0][key] as string, 10);
      }
    }

    if (!chartId) {
      chartId = widget.widget_id;
    }
    if (!chart) {
      chart = am4core.create(chartId, am4plugins_wordCloud.WordCloud);
    }
    const series = chart.series.push(new am4plugins_wordCloud.WordCloudSeries());
    series.accuracy = 0;
    series.step = 0;
    series.rotationThreshold = 0;
    series.maxCount = 1;
    series.minWordLength = 1;

    const data = res[0];
    const key = Object.keys(data)[0];
    series.labels.template.tooltipText = `${key}: [bold]{word}[/]`;
    series.labels.template.tooltipX = am4core.percent(50);
    series.labels.template.tooltipY = am4core.percent(0);
    // series.labels.template.tooltipX = am4core.percent(50);
    // series.labels.template.tooltipY = am4core.percent(0);
    series.labels.template.pointerOrientation = 'up';
    series.fontFamily = 'Courier New';
    series.maxFontSize = am4core.percent(100);
    series.data = [
      {
        tag: data[key].toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','),
        weight: 100
      }
    ];
    series.dataFields.word = 'tag';
    series.dataFields.value = 'weight';

    const wordCloudLabel = chart.chartContainer.createChild(am4core.Label);
    wordCloudLabel.text = key;
    wordCloudLabel.align = 'center';
    wordCloudLabel.valign = 'bottom';
    wordCloudLabel.fontSize = 18;
    wordCloudLabel.paddingBottom = 20;

    chart.data = res;
    return chart;
  }

  drawSolidGaugeChart(res, chart, widget, chartId?: string) {
    if (!chartId) {
      chartId = widget.widget_id;
    }
    if (!chart) {
      chart = am4core.create(chartId, am4charts.RadarChart);

      // Make chart not full circle
      chart.startAngle = -90;
      chart.endAngle = 180;
      chart.innerRadius = am4core.percent(20);

      // Set number format
      chart.numberFormatter.numberFormat = '#.#\'%\'';

      // Create axes
      const categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = 'name';
      categoryAxis.renderer.grid.template.location = 0;
      categoryAxis.renderer.grid.template.strokeOpacity = 0;
      categoryAxis.renderer.labels.template.horizontalCenter = 'right';
      categoryAxis.renderer.labels.template.fontWeight = 500;
      categoryAxis.renderer.labels.template.adapter.add('fill', (fill, target) => {
        return (target.dataItem.index >= 0) ? chart.colors.getIndex(target.dataItem.index) : fill;
      });
      categoryAxis.renderer.minGridDistance = 10;

      const valueAxis = chart.xAxes.push(new am4charts.ValueAxis());
      valueAxis.renderer.grid.template.strokeOpacity = 0;
      valueAxis.min = 0;
      valueAxis.max = 100;
      valueAxis.strictMinMax = true;

      const title = chart.titles.create();
      title.text = widget.title;
      // title.text = `${widget.params.field.alias? widget.params.field.alias.toUpperCase(): widget.params.field.friendly.toUpperCase()}`;
      title.fontSize = 16;
      title.fontWeight = 'bold';

      // Create series
      const series1 = chart.series.push(new am4charts.RadarColumnSeries());
      series1.dataFields.valueX = 'percentage';
      series1.dataFields.categoryY = 'name';
      series1.clustered = false;
      series1.columns.template.fill = new am4core.InterfaceColorSet().getFor('alternativeBackground');

      series1.columns.template.fillOpacity = 0.08;
      series1.columns.template.cornerRadiusTopLeft = 20;
      series1.columns.template.strokeWidth = 0;
      series1.columns.template.radarColumn.cornerRadius = 20;

      const series2 = chart.series.push(new am4charts.RadarColumnSeries());
      series2.dataFields.valueX = 'count';
      series2.dataFields.categoryY = 'name';
      series2.clustered = false;
      series2.columns.template.strokeWidth = 0;
      series2.columns.template.tooltipText = '{name}: [bold]{count}[/]';
      series2.columns.template.radarColumn.cornerRadius = 20;

      series2.columns.template.adapter.add('fill', (fill, target) => {
        return chart.colors.getIndex(target.dataItem.index);
      });
      // Add cursor
      chart.cursor = new am4charts.RadarCursor();
    }
    chart.data = res;
    return chart;
  }

  drawFuelGaugeChart(value, chart, chartId: string) {
    if (!chart) {
      chart = am4core.create(chartId, am4charts.GaugeChart);
      chart.innerRadius = -15;

      const axis = chart.xAxes.push(new am4charts.ValueAxis<am4charts.AxisRendererCircular>());
      axis.min = 0;
      axis.max = 100;
      axis.strictMinMax = true;

      const colorSet = new am4core.ColorSet();

      const gradient = new am4core.LinearGradient();
      gradient.stops.push({color: am4core.color('#0080DC')});
      gradient.stops.push({color: am4core.color('#009054')});
      gradient.stops.push({color: am4core.color('#E6A243')});
      gradient.stops.push({color: am4core.color('#C94218')});
      gradient.stops.push({color: am4core.color('#B50D12')});

      axis.renderer.line.stroke = gradient;
      axis.renderer.line.strokeWidth = 25;
      axis.renderer.line.strokeOpacity = 1;

      axis.renderer.grid.template.disabled = true;

      const hand = chart.hands.push(new am4charts.ClockHand());
      hand.radius = am4core.percent(75);

      setInterval(() => {
        hand.showValue(value, 1000, am4core.ease.cubicOut);
      }, 2000);
    }
    return chart;
  }

  lumenColorsTheme(target) {
    if (target instanceof am4core.ColorSet) {
      target.list = [
        am4core.color('#0075C9'),
        am4core.color('#009054'),
        am4core.color('#EE3026'),
        am4core.color('#B96B00'),
        am4core.color('#9557D4'),
        am4core.color('#DB1A5B')
      ];
    }
  }

}
